import { UserProfile } from '../types';

export const useProfileCompletion = (user: UserProfile | null) => {
  const address =
    user?.permanentAddress ||
    user?.address ||
    user?.residentialAddress ||
    user?.workAddress ||
    user?.schoolAddress ||
    user?.billingAddress;

  const hasAddress = Boolean(
    address?.street?.trim() &&
    address?.city?.trim() &&
    address?.country?.trim()
  );

  const hasIdentityVerification = Boolean(
    user?.userIdentityVerification?.id ||
    user?.userIdentityVerification?.isVerified ||
    user?.identityVerified ||
    user?.userIdentityVerification?.uploadedOn ||
    user?.userIdentityVerification?.verificationDecision === 'APPROVED' ||
    (user?.userIdentityVerification?.idDocumentIds?.length || 0) > 0 ||
    (user?.userIdentityVerification?.idDocumentUrls?.length || 0) > 0
  );

  const hasWorkAuthorization = Boolean(
    user?.userWorkAuthorization?.id ||
    user?.userWorkAuthorization?.isVerified ||
    user?.userWorkAuthorization?.uploadedOn ||
    (user?.userWorkAuthorization?.idDocumentIds?.length || 0) > 0
  );

  const hasHelperProfile = Boolean(
    user?.helperProfile?.id ||
    user?.helperProfile?.description ||
    user?.helperProfile?.bio ||
    user?.helperProfile?.headline ||
    (Array.isArray(user?.helperProfile?.skills) && user?.helperProfile?.skills.length > 0) ||
    (Array.isArray(user?.helperProfile?.helperSkills) && user?.helperProfile?.helperSkills.length > 0)
  );

  const role = user?.currentAppRole || 'REQUESTER';
  const requiredFields = [
    { field: 'firstName', label: 'First Name', completed: !!user?.firstName?.trim() },
    { field: 'lastName', label: 'Last Name', completed: !!user?.lastName?.trim() },
    { field: 'birthDate', label: 'Date of Birth', completed: !!user?.birthDate?.trim() },
    {
      field: 'gender',
      label: 'Gender',
      completed: !!user?.gender?.trim() && (user.gender !== 'OTHER' || !!user.otherGender?.trim()),
    },
    { field: 'email', label: 'Email', completed: !!user?.email?.trim() },
    { field: 'address', label: 'Address', completed: hasAddress },
    { field: 'profilePhoto', label: 'Profile Photo', completed: !!user?.profilePhoto || !!user?.picture },
    ...(role === 'HELPER'
      ? [
          { field: 'securityPhoto', label: 'Security Photo', completed: !!user?.securityPhoto },
          { field: 'identityVerification', label: 'Identity Verification', completed: hasIdentityVerification },
          { field: 'workAuthorization', label: 'Work Authorization', completed: hasWorkAuthorization },
          { field: 'helperProfile', label: 'Helper Profile', completed: hasHelperProfile },
        ]
      : []),
  ];

  const completedCount = requiredFields.filter(f => f.completed).length;
  const totalRequired = requiredFields.length;
  const completionPercentage = (completedCount / totalRequired) * 100;
  const isProfileComplete = completionPercentage === 100;

  const missingFields = requiredFields.filter(f => !f.completed);

  return {
    isProfileComplete,
    completionPercentage,
    missingFields,
    completedCount,
    totalRequired,
  };
};
